import {useCallback, useSyncExternalStore} from 'react'

import type {CellAddress, ItemId} from '../types'
import {useBoardFocusStore} from './contexts'

/** The answer for a cell the position is not in, and for a position on a cell rather than a card. */
const NOT_HERE = -1

/**
 * Where the board's focus position sits in one cell's cards, as an index into the very
 * `itemIds` that cell draws from, or `-1` when the position is not on a card here.
 *
 * **This is the one number a cell's card window and the focus model agree on.** A window
 * decides what to draw by index, and a position is held as identities, so something has to
 * turn one into the other.
 *
 * The reducer's own anchor answers it in one step, and is checked rather than trusted. The
 * anchor is settled against the grid and repaired whenever the caller's data moves, but the
 * repair is an effect, so there is one render -- the first with new data in it -- where the
 * anchor still describes the board as it was. Believing it there would pin a card that has
 * moved, or an index the cell no longer has at all, which is an index a window would be
 * asked to draw out of a list that is now shorter than it.
 *
 * So the anchor is used when it still names the same card, and the card is looked up
 * otherwise. The cheap answer is the one that is nearly always right, and the expensive one
 * is only paid on the render after the caller's data changed.
 *
 * A number rather than the position itself, and that is the point: every drawn cell
 * subscribes to this, and a snapshot that returned an object would compare unequal every
 * time and wake all of them. Only the cells whose answer changed re-render, which on a move
 * is the one being left and the one being arrived at.
 */
export function useFocusedCardIndex({columnKey, rowKey}: CellAddress, itemIds: readonly ItemId[]): number {
  const {getState, subscribe} = useBoardFocusStore()

  const getSnapshot = useCallback(() => {
    const {position, anchor} = getState()
    // The cheap halves first: every cell on screen runs this on every move, and only one of
    // them should pay for looking anything up.
    if (position === null || position.itemId === null) return NOT_HERE
    if (position.columnKey !== columnKey || position.rowKey !== rowKey) return NOT_HERE

    if (itemIds[anchor.itemIndex] === position.itemId) return anchor.itemIndex

    // `-1` when the cell no longer holds it, which is the same answer as the position being
    // somewhere else: there is no card here to draw.
    return itemIds.indexOf(position.itemId)
  }, [getState, columnKey, rowKey, itemIds])

  // The same snapshot on the server: the reducer seeds its state from the caller's data, so
  // both sides answer from the same board.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Whether focus has been asked for on a card in this cell and nothing has taken it yet.
 *
 * **The other half of {@link useFocusedCardIndex}, and the reason a cell's card window can
 * move.** The index alone says where the position is; this says the position is owed an
 * element. A window scrolls for the second and never for the first, because the index also
 * changes when the caller's data shifts the card the position was pointing at, and dragging
 * a reader's cell somewhere because somebody else moved a card is not something they asked
 * for. The board's own two axes read the same distinction off `pendingFocus`.
 *
 * A boolean, and scoped to one cell, so the only cell it ever wakes is the one the position
 * is in: every other cell answers `false` on every move and `useSyncExternalStore` bails
 * out of the render.
 */
export function useIsCardFocusPending({columnKey, rowKey}: CellAddress): boolean {
  const {getState, subscribe} = useBoardFocusStore()

  const getSnapshot = useCallback(() => {
    const {position, pendingFocus} = getState()
    if (!pendingFocus || position === null || position.itemId === null) return false

    return position.columnKey === columnKey && position.rowKey === rowKey
  }, [getState, columnKey, rowKey])

  // The same snapshot on the server, where nothing is pending because nothing has moved.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
